/**
 * Proxy, email, and phone checks with no database import.
 * The admin UI can bundle this file; `accounts.ts` re-exports it.
 */

/** Playwright's proxy option shape. */
export interface ProxyConfig {
  server: string;
  username?: string | null;
  password?: string | null;
}

/**
 * Normalize proxy config into Playwright's `{server, username?, password?}` shape.
 */
export function normalizeProxy(proxy: unknown): ProxyConfig | null {
  if (proxy == null || proxy === "") return null;

  if (typeof proxy === "string") {
    return parseProxyUrl(proxy.trim());
  }

  if (typeof proxy === "object") {
    const raw = proxy as {
      server?: unknown;
      username?: unknown;
      password?: unknown;
    };
    const server = String(raw.server || "").trim();
    if (!server) {
      throw new Error("proxy.server is required");
    }
    const out: ProxyConfig = { server };
    if (raw.username) out.username = String(raw.username);
    if (raw.password != null && raw.password !== "") {
      out.password = String(raw.password);
    }
    return out;
  }

  throw new Error("proxy must be a URL string, object, or null");
}

function parseProxyUrl(urlString: string): ProxyConfig {
  let url: URL;
  try {
    url = new URL(urlString);
  } catch {
    throw new Error(`invalid proxy URL "${urlString}"`);
  }

  if (!/^https?:$|^socks5?:$/i.test(url.protocol)) {
    throw new Error(
      `unsupported proxy protocol "${url.protocol}" (use http, https, socks5)`,
    );
  }

  if (!url.hostname || !url.port) {
    throw new Error(`proxy URL must include host and port: "${urlString}"`);
  }

  const server = `${url.protocol}//${url.hostname}:${url.port}`;
  const out: ProxyConfig = { server };
  if (url.username) out.username = decodeURIComponent(url.username);
  if (url.password) out.password = decodeURIComponent(url.password);
  return out;
}

/** Safe string for logs — never includes credentials. */
export function proxyLabel(proxy?: ProxyConfig | null): string {
  if (!proxy?.server) return "direct";
  try {
    const u = new URL(proxy.server);
    return `${u.protocol}//${u.host}`;
  } catch {
    return proxy.server;
  }
}

/**
 * Full proxy URL including credentials — for settings UI round-trip.
 * Keeps `;` etc. literal in userinfo so pasted DataImpulse URLs match.
 */
export function proxyUrl(proxy?: ProxyConfig | null): string | null {
  if (!proxy?.server) return null;
  try {
    const u = new URL(proxy.server);
    const base = `${u.protocol}//${u.host}`;
    if (!proxy.username) return base;
    const user = String(proxy.username);
    if (proxy.password != null && proxy.password !== "") {
      return `${u.protocol}//${user}:${String(proxy.password)}@${u.host}`;
    }
    return `${u.protocol}//${user}@${u.host}`;
  } catch {
    return proxy.server;
  }
}

/**
 * Basic email shape check for account logins. Catches the classic
 * `mikepolish66yahoo.com` typo (missing "@") while staying permissive about
 * dot-free domains and non-RFC exotics.
 */
const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

/** True when the string looks like a well-formed email address. */
export function isValidEmail(value: unknown): boolean {
  return EMAIL_RE.test(String(value || "").trim());
}

/** Loose phone check — digits, spaces, "+", "-" or "()" with 7+ digits. */
export function isValidPhone(value: unknown): boolean {
  const s = String(value || "").trim();
  if (!/^[0-9+()\-\s.]{7,25}$/.test(s)) return false;
  const digits = s.replace(/\D/g, "");
  return digits.length >= 7 && digits.length <= 15;
}
